import { Activity, BarChart2, DollarSign, TrendingDown, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import api from "../../services/api";
import { safeNumber } from "../../utils/helpers";
import TradingCalendar from "../calendar/TradingCalendar";
import DailyGoalWidget from './DailyGoalWidget';
import OnboardingWizard from '../onboarding/OnboardingWizard';
import { useAuth } from "../../context/AuthContext";

/**
 * Modern Dashboard Component
 * Features: Glassmorphism, Responsive Grid, Enhanced Charts, Monetization Slots
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [period, setPeriod] = useState("month");
  const [customDates, setCustomDates] = useState({ start: "", end: "" });
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [metrics, setMetrics] = useState({
    totalPnl: 0,
    totalPositions: 0,
    winningPositions: 0,
    losingPositions: 0,
    winRate: 0,
    avgWin: 0,
    avgLoss: 0,
    profitFactor: 0,
    largestWin: 0,
    largestLoss: 0,
    openPositions: 0,
    closedPositions: 0,
  });
  const [symbolPerformance, setSymbolPerformance] = useState({
    topPerformers: [],
    worstPerformers: [],
  });
  const [pnlChart, setPnlChart] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchSymbolPerformance();
    fetchPnlChart();
  }, [period, customDates]);

  // Show onboarding wizard for new users who have no positions and haven't completed onboarding
  useEffect(() => {
    if (!loading && metrics.totalPositions === 0 && user && !user?.preferences?.onboarding_completed) {
      setShowOnboarding(true);
    }
  }, [loading, metrics.totalPositions, user]);

  const fetchDashboardData = async () => {
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom" && customDates.start && customDates.end) {
        params.append("startDate", customDates.start);
        params.append("endDate", customDates.end);
      }

      const { data } = await api.get(`/dashboard/summary?${params}`);
      if (data.success) {
        setMetrics(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSymbolPerformance = async () => {
    try {
      const params = new URLSearchParams({ period, limit: 5 });
      if (period === "custom" && customDates.start && customDates.end) {
        params.append("startDate", customDates.start);
        params.append("endDate", customDates.end);
      }

      const { data } = await api.get(`/dashboard/symbol-performance?${params}`);
      if (data.success) {
        setSymbolPerformance(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch symbol performance:", error);
    }
  };

  const fetchPnlChart = async () => {
    try {
      const params = new URLSearchParams({ period: "daily" });
      if (period === "custom" && customDates.start && customDates.end) {
        params.append("dateFrom", customDates.start);
        params.append("dateTo", customDates.end);
      } else {
        const endDate = new Date();
        let startDate = new Date();
        switch (period) {
          case "week":
            startDate.setDate(startDate.getDate() - 7);
            break;
          case "month":
            startDate.setDate(startDate.getDate() - 30);
            break;
          case "year":
          case "ytd":
            startDate = new Date(endDate.getFullYear(), 0, 1);
            break;
        }
        params.append("dateFrom", startDate.toISOString().split("T")[0]);
        params.append("dateTo", endDate.toISOString().split("T")[0]);
      }

      const { data } = await api.get(`/analytics/pnl-over-time?${params}`);
      if (data.success) {
        setPnlChart(data.data.pnlChart || []);
      }
    } catch (error) {
      console.error("Failed to fetch P&L chart:", error);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod !== "custom") {
      setCustomDates({ start: "", end: "" });
      setShowCustomDates(false);
    } else {
      setShowCustomDates(true);
    }
  };

  const MetricCard = ({ title, value, subtitle, trend, icon: Icon }) => (
    <div className="relative overflow-hidden rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] bg-slate-800/50 backdrop-blur-md border border-slate-700/50 shadow-xl shadow-black/10">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-slate-400 text-sm font-medium">{title}</h3>
        <div className="p-2 rounded-lg bg-slate-700/50 text-slate-400">
          {Icon && <Icon size={20} />}
        </div>
      </div>
      
      <div className="space-y-1">
        <div className="text-2xl font-bold text-slate-100 tracking-tight">
          {value}
        </div>
        {subtitle && (
          <div className="flex items-center gap-2 text-sm font-medium">
            {trend === 'up' && <TrendingUp size={14} className="text-emerald-400" />}
            {trend === 'down' && <TrendingDown size={14} className="text-red-400" />}
            <span className={trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-slate-500'}>
              {subtitle}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const AdSlotCard = () => (
    <div className="relative overflow-hidden rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] bg-gradient-to-br from-indigo-900/40 via-purple-900/20 to-slate-800/80 border border-indigo-500/30 group shadow-lg shadow-indigo-500/10">
      {/* Animated Glowing Background Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-2xl"></div>
      
      {/* Floating Badge */}
      <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1 bg-indigo-500/20 border border-indigo-500/40 rounded-full shadow-[0_0_15px_rgba(99,102,241,0.2)]">
        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse"></span>
        <span className="text-[10px] uppercase tracking-wider font-bold text-indigo-300">Pro Feature</span>
      </div>

      <div className="relative z-10 flex flex-col h-full justify-between">
        <div>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-5 shadow-lg shadow-indigo-500/40 transform group-hover:-translate-y-1 transition-transform">
            <TrendingUp size={24} className="text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2 tracking-tight group-hover:text-indigo-200 transition-colors">Unlock AI Analytics</h3>
          <p className="text-sm text-indigo-200/70 leading-relaxed mb-6">
            Get personalized smart recommendations, instantly pinpoint hidden trading mistakes, and maximize your edge.
          </p>
        </div>
        
        <button className="w-full py-3 px-4 bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-bold rounded-xl transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] flex items-center justify-center gap-2 group-hover:bg-indigo-400">
          Upgrade Now
          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const chartData = pnlChart.map((item) => ({
    name: new Date(item.date).toLocaleDateString(),
    pnl: item.pnl,
  }));

  return (
    <div className="space-y-6">
      {/* Onboarding Wizard — shown as modal overlay for new users */}
      {showOnboarding && <OnboardingWizard onComplete={() => setShowOnboarding(false)} />}

      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Trading Performance</h1>
          <p className="text-slate-400 text-sm">Overview of your trading activity</p>
        </div>

        <div className="flex items-center gap-1 bg-slate-800/50 p-1 rounded-xl border border-slate-700/50 overflow-x-auto">
          {["week", "month", "year", "ytd", "custom"].map((p) => (
            <button
              key={p}
              onClick={() => handlePeriodChange(p)}
              className={`
                px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap
                ${period === p
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                }
              `}
            >
              {p === "ytd" ? "YTD" : p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {showCustomDates && (
        <div className="flex gap-4 p-4 bg-slate-800/30 rounded-xl border border-slate-700/30 animate-in fade-in slide-in-from-top-2">
          <div className="flex-1">
            <label className="block text-xs text-slate-400 mb-1">Start Date</label>
            <input
              type="date"
              value={customDates.start}
              onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 focus:border-emerald-500 outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-slate-400 mb-1">End Date</label>
            <input
              type="date"
              value={customDates.end}
              onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 focus:border-emerald-500 outline-none"
            />
          </div>
        </div>
      )}

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total P&L"
          value={`$${safeNumber(metrics.totalPnl, 0).toFixed(2)}`}
          subtitle={`${metrics.closedPositions} closed positions`}
          trend={metrics.totalPnl >= 0 ? "up" : "down"}
          icon={DollarSign}
        />
        <MetricCard
          title="Win Rate"
          value={`${safeNumber(metrics.winRate, 0).toFixed(1)}%`}
          subtitle={`${metrics.winningPositions}W / ${metrics.losingPositions}L`}
          trend={metrics.winRate >= 50 ? "up" : "down"}
          icon={Activity}
        />
        <MetricCard
          title="Profit Factor"
          value={safeNumber(metrics.profitFactor, 0).toFixed(2)}
          subtitle={metrics.profitFactor >= 1.5 ? "Excellent" : "Needs Work"}
          icon={BarChart2}
        />
        {/* Monetization / Ad Slot */}
        <AdSlotCard />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
          <p className="text-slate-500 text-xs mb-1">Avg Win</p>
          <p className="text-lg font-bold text-emerald-400">${safeNumber(metrics.avgWin, 0).toFixed(2)}</p>
        </div>
        <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
          <p className="text-slate-500 text-xs mb-1">Avg Loss</p>
          <p className="text-lg font-bold text-red-400">${safeNumber(metrics.avgLoss, 0).toFixed(2)}</p>
        </div>
        <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
          <p className="text-slate-500 text-xs mb-1">Best Trade</p>
          <p className="text-lg font-bold text-emerald-400">
            {metrics.largestWin > 0 ? `$${safeNumber(metrics.largestWin, 0).toFixed(2)}` : 'N/A'}
          </p>
        </div>
        <div className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/30">
          <p className="text-slate-500 text-xs mb-1">Worst Trade</p>
          <p className="text-lg font-bold text-red-400">
            {metrics.largestLoss < 0 ? `$${safeNumber(metrics.largestLoss, 0).toFixed(2)}` : 'N/A'}
          </p>
        </div>
      </div>


      {/* Daily Goal Tracker */}
      <div className="mb-6">
        <DailyGoalWidget />
      </div>
      {/* Empty state — onboarding complete but no trades yet */}
      {metrics.totalPositions === 0 && user?.preferences?.onboarding_completed && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
          <div className="text-4xl mb-3">📊</div>
          <h3 className="text-white font-semibold mb-2">No trades yet</h3>
          <p className="text-slate-400 mb-4">Log your first trade or connect a broker to get started.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/brokers')}
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              Connect Broker
            </button>
            <button
              onClick={() => navigate('/trades')}
              className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              + Log Trade
            </button>
          </div>
        </div>
      )}

      {/* Charts Section */}
      {chartData.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6">
          <h2 className="text-lg font-bold text-slate-100 mb-6">P&L Overview</h2>
          <div className="h-48 md:h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  stroke="#94a3b8" 
                  fontSize={12} 
                  tickLine={false} 
                  axisLine={false}
                />
                <YAxis 
                  stroke="#94a3b8" 
                  fontSize={12} 
                  tickLine={false} 
                  axisLine={false}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  itemStyle={{ color: '#e2e8f0' }}
                  formatter={(value) => [`$${value.toFixed(2)}`, "P&L"]}
                />
                <Area 
                  type="monotone" 
                  dataKey="pnl" 
                  stroke="#10b981" 
                  strokeWidth={2} 
                  fillOpacity={1} 
                  fill="url(#colorPnl)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Performance Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6">
          <h3 className="text-emerald-400 font-bold mb-4 flex items-center gap-2">
            <TrendingUp size={18} /> Top Performers
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-slate-500 border-b border-slate-700">
                  <th className="pb-3 font-medium">Symbol</th>
                  <th className="pb-3 font-medium text-right">P&L</th>
                  <th className="pb-3 font-medium text-right">Win Rates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {symbolPerformance.topPerformers.map((symbol) => (
                  <tr key={symbol.symbol} className="hover:bg-slate-700/20 transition-colors">
                    <td className="py-3 font-medium text-slate-200">{symbol.symbol}</td>
                    <td className="py-3 text-right text-emerald-400 font-medium">
                      ${symbol.totalPnl.toFixed(2)}
                    </td>
                    <td className="py-3 text-right text-slate-400">{symbol.winRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Worst Performers */}
        <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6">
          <h3 className="text-red-400 font-bold mb-4 flex items-center gap-2">
            <TrendingDown size={18} /> Underperformers
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-slate-500 border-b border-slate-700">
                  <th className="pb-3 font-medium">Symbol</th>
                  <th className="pb-3 font-medium text-right">P&L</th>
                  <th className="pb-3 font-medium text-right">Win Rates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {symbolPerformance.worstPerformers.filter(s => s.totalPnl < 0).length === 0 ? (
                  <tr><td colSpan={3} className="py-4 text-center text-slate-500">No underperforming symbols</td></tr>
                ) : (
                  symbolPerformance.worstPerformers.filter(s => s.totalPnl < 0).map((symbol) => (
                    <tr key={symbol.symbol} className="hover:bg-slate-700/20 transition-colors">
                      <td className="py-3 font-medium text-slate-200">{symbol.symbol}</td>
                      <td className={`py-3 text-right font-medium ${symbol.totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        ${symbol.totalPnl.toFixed(2)}
                      </td>
                      <td className="py-3 text-right text-slate-400">{symbol.winRate}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-8">
         {/* Simple container for the calendar until it is modernized */}
         <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6">
            <h2 className="text-lg font-bold text-slate-100 mb-6">Trading Calendar</h2>
            <TradingCalendar />
         </div>
      </div>

    </div>
  );
};

export default Dashboard;
